import React, { useRef, useEffect, useState } from 'react';
import Hls from 'hls.js';
import { Play, Pause, Volume2, VolumeX, Maximize } from 'lucide-react';

// Détecte le type de source vidéo
const getVideoType = (src) => {
  if (!src) return 'unknown';
  if (src.includes('youtube.com') || src.includes('youtu.be')) return 'youtube';
  if (src.includes('facebook.com') || src.includes('fb.watch')) return 'facebook';
  if (src.endsWith('.m3u8') || src.includes('/master.m3u8')) return 'hls';
  return 'hls'; // Par défaut HLS via backend stream
};

// Extrait l'ID YouTube pour construire l'URL embed
const getYouTubeEmbedUrl = (url) => {
  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
  return match ? `https://www.youtube.com/embed/${match[1]}?autoplay=0&rel=0` : url;
};

// Extrait l'URL embed Facebook
const getFacebookEmbedUrl = (url) => {
  return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&width=720`;
};

export const HLSPlayer = ({ src, onTimeUpdate }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [progress, setProgress] = useState(0);

  const videoType = getVideoType(src);
  const isEmbed = videoType === 'youtube' || videoType === 'facebook';

  // Hooks toujours appelés, même pour les embeds (règle des Hooks)
  useEffect(() => {
    if (isEmbed) return;
    const video = videoRef.current;
    if (!video) return;

    // Disable context menu (no download)
    video.addEventListener('contextmenu', (e) => e.preventDefault());

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 0,
        maxBufferLength: 10,
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      return () => hls.destroy();
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
    }
  }, [src, isEmbed]);

  useEffect(() => {
    if (isEmbed) return;
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const percent = (video.currentTime / video.duration) * 100;
      setProgress(percent);
      if (onTimeUpdate) {
        onTimeUpdate(video.currentTime);
      }
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => video.removeEventListener('timeupdate', handleTimeUpdate);
  }, [onTimeUpdate, isEmbed]);

  // Pour les embeds YouTube/Facebook, rendu iframe
  if (videoType === 'youtube') {
    return (
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={getYouTubeEmbedUrl(src)}
          className="absolute inset-0 w-full h-full rounded-xl"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YetuTv YouTube Player"
        />
      </div>
    );
  }

  if (videoType === 'facebook') {
    return (
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={getFacebookEmbedUrl(src)}
          className="absolute inset-0 w-full h-full rounded-xl"
          frameBorder="0"
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
          allowFullScreen
          title="YetuTv Facebook Player"
        />
      </div>
    );
  }

  const togglePlay = () => {
    const video = videoRef.current;
    if (video.paused) {
      video.play();
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    video.muted = !video.muted;
    setMuted(video.muted);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const handleProgressClick = (e) => {
    const video = videoRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    video.currentTime = percent * video.duration;
  };

  return (
    <div
      className="relative w-full bg-black rounded-xl overflow-hidden"
      data-testid="video-player"
    >
      <video
        ref={videoRef}
        className="w-full aspect-video"
        playsInline
        controlsList="nodownload noplaybackrate"
        onContextMenu={(e) => e.preventDefault()}
      />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
        <div
          className="w-full h-1 bg-white/30 rounded-full cursor-pointer mb-3"
          onClick={handleProgressClick}
          data-testid="video-player-progress"
        >
          <div
            className="h-full bg-primary rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={togglePlay}
            className="text-white hover:text-primary transition-colors"
            data-testid="video-player-play"
          >
            {playing ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
          </button>
          <button
            onClick={toggleMute}
            className="text-white hover:text-primary transition-colors"
            data-testid="video-player-volume"
          >
            {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
          </button>
          <div className="flex-1" />
          <button
            onClick={toggleFullscreen}
            className="text-white hover:text-primary transition-colors"
            data-testid="video-player-fullscreen"
          >
            <Maximize className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
};
